import React, { act, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { UserDetailsContext } from '../../App';
import { openDB } from 'idb';
import { getHomeLink, parseJwt } from '../../helper/genericFunction';
import SideMenuIcons, { SideMenuFilledIcons } from '../../assets/sideMenuIcons';
import Icons from '../../assets/icons';
import { ArrowLeft, Bolt, CheckCheck, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Compass, Component, Cross, ExternalLink, Grip, HomeIcon, Menu, MoveUpRight, Plus, Sparkles, X } from 'lucide-react';
import GenericCustomButton from '../../genericComponents/GenericCustomButton';
import ImgPath from '../../assets/images'
import { Switch, Tooltip } from 'antd';
import { AnimatePresence, hover, motion } from 'framer-motion';

import GenericInput from '../../genericComponents/input/GenericInputComponent';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { getDataStorageKey, getMenuMode } from '../../helper/helperTableFunctions';
import { getNumberOfStrips } from '../../initialPhaseOperations/InitialAnnouncementModal';
import LanguageTranslator from '../../locals/LanguageTranslator';
import { stacksV2 } from '../../constants/routingStackV2';
// import useCachedBase64Image from '../../helper/useCachedBase64Image ';

const getOfflineStorageKey = () => {
	const uId = parseJwt(sessionStorage?.getItem?.('token'))?.jti;
	return `mainMenuLocalState+${uId}`
}

// Reverse lookup: route path -> set of workspace module codes that own it,
// derived from stacksV2. Report routes are spread (...reportStack) into several
// workspace modules, so a single path can legitimately belong to more than one.
const pageUrlToModuleCodes = (() => {
	const map = {};
	Object.keys(stacksV2 || {}).forEach(moduleCode => {
		Object.keys(stacksV2[moduleCode] || {}).forEach(stackKey => {
			(stacksV2[moduleCode][stackKey] || []).forEach(path => {
				if (!path) return;
				if (!map[path]) map[path] = new Set();
				map[path].add(moduleCode);
			});
		});
	});
	return map;
})();

// Given a page url (with or without leading '#') and the currently active
// module, decide whether we need to switch workspaces before navigating.
// Returns a target module code only when the active module does NOT own the
// path but some other module does; returns null when the active module already
// owns it (e.g. a report route shared across workspaces) or when the path is
// unknown — in both cases a plain history.push is correct.
const resolveWorkspaceSwitchTarget = (pageUrl = "", activeModule = "") => {
	const clean = pageUrl?.replace(/^#/, "") || "";
	const owners = pageUrlToModuleCodes[clean];
	if (!owners || owners.has(activeModule)) return null;
	// Deterministic pick when multiple modules own it but none is active.
	return [...owners][0] || null;
};

// Function : To get the dashboard path 
const getDashboardPath = (userDetails, history, expandMode, setMenuExpanded, force, menuMode) => {
	let activeHome = history.location.pathname.split('/')?.[4] == "dashboard";
	if (activeHome && !force) return;
	let dashboardModules = null;
	// Function to move the route to home
	if (menuMode == "new") {
		let modules = JSON.parse(userDetails?.["mainMenu"] || "{}");
		dashboardModules = modules?.["dashboardModules"] || [];
	} else {
		dashboardModules = JSON.parse(userDetails?.["dashboardModules"] || "[]");
	}
	if (dashboardModules?.length !== 0) {
		let pinnedModules = localStorage.getItem(parseJwt(sessionStorage.getItem('token')).jti), pageUrl = "";
		if (pinnedModules) {
			let id = Number(pinnedModules.split(",")[0]);
			let page_Url = dashboardModules.find(item => item.id === id).pageUrl;
			pageUrl = page_Url?.replace(/^#/, '');
		}
		else {
			let page_Url = dashboardModules[0].pageUrl;
			pageUrl = page_Url?.replace(/^#/, '');
		}
		if (expandMode == "normal") {
			setMenuExpanded?.(false);
		}
		history.push(pageUrl);
		return;
	}
	if (expandMode == "normal") {
		setMenuExpanded?.(false);
	}
	history.push(getMenuMode?.() == "new" ? "/app" : "/home");
}

const MainMenu = (props = {}) => {

	const [stripHeight, setStripHeight] = useState(getNumberOfStrips() || 0);

	const { getNewMainMenuModulesRequest, currentModuleRef } = props;

	let { userDetails, setUserDetails } = useContext(UserDetailsContext)

	const uType = parseJwt(sessionStorage.getItem("token"))?.uType?.toUpperCase?.();

	const [modules, setModules] = useState(JSON.parse(userDetails?.mainMenu || "[]"))
	// console.log('userDetails?.mainMenu', JSON.parse(userDetails?.mainMenu || "[]"))
	const [overlayModules, setOverlayModules] = useState({ 
		ADMIN: JSON.parse(userDetails?.adminModules || "[]"),
		MDM: JSON.parse(userDetails?.mdmModules || "[]")
	})
	const [isHoveringOverAdminMaster, setIsHoveringOverAdminMaster] = useState(false)
	const [overlayActiveModule, setOverlayActiveModule] = useState('ADMIN');
	const containerRef = useRef(null)
	const moduleContentRef = useRef(null)
	const [topValue, setTopValue] = useState(0)
	const [maxScroll, setMaxScroll] = useState(0)

	// Function :  To check if the menu is expanded or not
	const isMenuExpanded = () => {
		if (expandMode == "settings" || expandMode == "reports") {
			return true;
		}
		else return menuExpanded;
	}

	useEffect(() => {
		if (!isMenuExpanded?.() && containerRef.current && moduleContentRef.current) {
			const containerHeight = containerRef.current.clientHeight
			const contentHeight = moduleContentRef.current.scrollHeight
			const maxScrollDistance = Math.max(0, contentHeight - containerHeight)
			setMaxScroll(maxScrollDistance)
		}
	}, [modules?.["moduleList"]?.[0]])

	const handleMouseMove = (e) => {

		if ((isMenuExpanded?.() && expandMode !== 'reports') || !containerRef.current) return;


		const rect = containerRef.current.getBoundingClientRect()
		const mouseY = e.clientY - rect.top
		const containerHeight = rect.height

		// Calculate scroll position based on mouse Y position (0 to 1)
		const scrollRatio = Math.max(0, Math.min(1, mouseY / containerHeight))

		// max scroll = content height - container height
  		const maxScroll = moduleContentRef.current.scrollHeight - containerRef.current.clientHeight;

		const newTopValue = scrollRatio * maxScroll

		setTopValue(newTopValue)
	}

	const handleMouseLeave = () => {
		if (isMenuExpanded?.() && expandMode !== 'reports') return;
		setTopValue(0)
	}

	const [activeModule, setActiveModule] = useState(props.currentActiveModule || "");
	const [activeSubModule, setActiveSubModule] = useState("");
	const [lastActiveModuleStates, setLastActiveModuleStates] = useState(null);

	const [expandMode, setExpandMode] = useState("normal");

	const [menuExpanded, setMenuExpanded] = useState(false);

	const [expandedModules, setExpandedModules] = useState({});

	const [skipCallAfterModuleSwitch, setSkipCallAfterModuleSwitch] = useState(false);

	const [search, setSearch] = useState("");
	const [modulesJson, setModulesJson] = useState({});

	// const [sidebarWidth, setSidebarWidth] = useState(63);
	// const contentRef = useRef(null);

	const history = useHistory();

	const componentStateRef = useRef("initial")

	const location = useLocation();

	const { getFirstPath } = useMostBasicFirstPathForTheCurrentWorkspace();

	useEffect(() => {
		currentModuleRef.current = activeModule;
	}, [activeModule])

	useEffect(() => {
		if (expandMode == "reports") {
			let subModules = getCurrenrtActiveSubModuleData?.();
			let obj = {};
			subModules?.subModules?.map(item => {
				obj[item?.code] = item;
			})
			setExpandedModules(obj);
		}
	}, [expandMode])

	useEffect(() => {
		setInterval(() => {
			setStripHeight(getNumberOfStrips())
		}, 400)
		if (props.currentActiveModule) {
			updateCurrentStates?.();
		}
	}, [])

	const goToFirstPageAvailable = (details = userDetails) => {
		let pagePath = getFirstPath(details);
		if (pagePath) {
			history.push(`${pagePath}`);
		} else {
			history.push(getMenuMode?.() == "new" ? "/app" : "/home");
		}
	}

	const updateUserDetailsAndOfflineStorage = () => {
		// let tempUserDetail = userDetails;
		// update the same data into the user details context for easy access
		setUserDetails(prev => ({
			...prev,
			"current_main_menu_local_config": getNewModulesJson?.()
		}))

		updateOfflineStorage?.()
	}

	useEffect(() => {
		if (componentStateRef?.current != "initial") {
			updateUserDetailsAndOfflineStorage()
		}
	}, [activeModule, activeSubModule, expandMode, expandedModules, lastActiveModuleStates])
	// As the expand mode or expanded menu state gets changes the search should also be cleared
	useEffect(() => {
		setSearch("");
	}, [expandMode, menuExpanded])


	useEffect(() => {
		let obj = {};

		const updateObject = (module, mainCode) => {
			module?.map(item => {
				let updatedCode = mainCode || item?.code;
				if (!obj?.[updatedCode]) obj[updatedCode] = {};
				if (item?.isPage == 1) {
					obj[updatedCode][`${item.pageUrl}`] = true;
				} else {
					updateObject(item?.subModules, item?.code);
				}
			})
		}
		updateObject(modules?.moduleList?.[0]?.[0]?.subModules);

		setModulesJson(obj);
		console.log("check234", modulesJson);
	}, [expandMode])

	// Function : To check if the current item is reports and analytics
	const isCurrentItemIsReportsAndAnalytics = (item = {}) => {
		// Check for the category is present or not 
		if (["RDREPOMAIN", "VDREPOMAIN", "CDREPOMAIN"]?.includes(item?.["code"])) {
			return true;
		}
		return false;
	}

	// useEffect(() => {
	// 	if (isMenuExpanded?.() && contentRef.current) {
	// 		// Get the full width of sidebar content
	// 		setSidebarWidth(contentRef.current.scrollWidth);
	// 	} else {
	// 		setSidebarWidth(63);
	// 	}
	// }, [menuExpanded, activeModule]);

	const getNewModulesJson = () => {
		let payload = {
			activeModule,
			activeSubModule,
			expandedModules,
			expandMode,
			lastActiveModuleStates,
			search,
			menuExpanded,
		}

		return payload;
	}

	useEffect(() => {
		if (!skipCallAfterModuleSwitch) {
			getModuleInformation()
		} else {
			setSkipCallAfterModuleSwitch(false);
		}
	}, [activeModule])

	// When the user navigates back to a dashboard URL (e.g. via breadcrumb Back button)
	// while the admin/settings workspace is still active, reset the menu state so the
	// left nav collapses the admin section and the dashboard content renders correctly.
	useEffect(() => {
		if (componentStateRef.current === 'initial') return;
		const isDashboardPath = location.pathname.split('/')?.[4] === 'dashboard';
		if (isDashboardPath && expandMode === 'settings') {
			if (lastActiveModuleStates?.activeModule) {
				setSkipCallAfterModuleSwitch(true);
				setActiveModule(lastActiveModuleStates?.activeModule);
				setModules(lastActiveModuleStates?.modules);
				setUserDetails(prev => ({
					...prev,
					"mainMenu": lastActiveModuleStates?.mainMenu,
				}));
			}
			setExpandMode("normal");
			setMenuExpanded(false);
		}
	}, [location.pathname])

	// Function : to get the module information
	const getModuleInformation = () => {
		if (modules?.activeModule != activeModule && activeModule) {
			getNewMainMenuModulesRequest(activeModule);
		}
	}

	const getDb = async () => {
		const db = await openDB(getDataStorageKey?.(), 1, {
			upgrade(db) {
				db.createObjectStore("LOCAL_CONFIG");
			}
		});
		return db;
	}

	// Function : To update the state in the offlinestorage
	const updateOfflineStorage = async (data) => {
		const db = await getDb?.();
		await addKeyToDatabase(db, getOfflineStorageKey?.(), getNewModulesJson?.(), "LOCAL_CONFIG");
		db?.close();
	}

	const addKeyToDatabase = async (db, itemKey, itemValue, storeKey) => {
		const value = await db.get(storeKey, itemKey)
		if (!value) {
			await db.add(storeKey, itemValue || {}, itemKey)
		} else {
			await db.put(storeKey, itemValue || {}, itemKey)
		}
	}

	// Function : to check if the pagee should be redirected to first page
	const shouldGoToFirstPage = () => {
		if (getMenuMode?.() == "new") {
			if (localStorage.getItem("go_to_first_page") == "true") {
				localStorage.removeItem("go_to_first_page")
				return true;
			}
		}
		localStorage.removeItem("go_to_first_page")
		return false;
	}

	// Function : To update the current states based on the offline storage
	const updateCurrentStates = async () => {
		const db = await getDb?.();
		const dbData = await db.get("LOCAL_CONFIG", getOfflineStorageKey?.())

		componentStateRef.current = "menu"

		let moduleValue = sessionStorage.getItem("login_user_first_time") ? modules?.activeModule : dbData?.activeModule || modules?.activeModule 
		let modifiedExpandMode = sessionStorage.getItem("login_user_first_time") ? "normal" : dbData?.expandMode;

		console.log("modifiedExpandMode",sessionStorage.getItem("login_user_first_time"));
		if(!sessionStorage.getItem("login_user_first_time")){
			if (moduleValue) {
				setActiveModule(moduleValue);
				dbData?.activeSubModule && setActiveSubModule(dbData?.activeSubModule);
				dbData?.expandedModules && setExpandedModules(dbData?.expandedModules);
				dbData?.lastActiveModuleStates && setLastActiveModuleStates(dbData?.lastActiveModuleStates);
				dbData?.search && setSearch(dbData?.search);
				if(modifiedExpandMode == "reports"){
					setExpandMode("normal");
					setMenuExpanded(false);
				}else{
					modifiedExpandMode && setExpandMode(modifiedExpandMode);
					dbData?.menuExpanded && setMenuExpanded(dbData?.menuExpanded)
				}
			}
		}else{
			setActiveModule(moduleValue);
		}
		let tokenData = parseJwt(sessionStorage.getItem("token"));
			let updatedUsersDetails = userDetails;
			setUserDetails(prev => {
				updatedUsersDetails = { ...prev, menuMode: localStorage.getItem(`menuMode-${tokenData?.jti}`) == "new" ? "new" : "old" }
				return updatedUsersDetails;
			})
			// AppStack sets this flag after honoring a pending_admin_navigation
			// push, so we don't immediately overwrite the user-clicked admin
			// page with the first-available one.
			const skipFirstPageRedirect = sessionStorage.getItem("skip_admin_first_page_redirect") === "true";
			if (skipFirstPageRedirect) {
				sessionStorage.removeItem("skip_admin_first_page_redirect");
			}
			if ((shouldGoToFirstPage?.() || modifiedExpandMode == "settings") && !skipFirstPageRedirect) {
				goToFirstPageAvailable(updatedUsersDetails);
			}
		sessionStorage.removeItem("login_user_first_time")
		
		db?.close?.()
	}

	// Function : to render the icon for the module.
	const renderIcon = (module) => {

		if (module?.icon_url == "create-icon.svg") {
			// Render the create icons
			return <div className='relative'>
				<div className='h-[22px] w-auto aspect-square p-[2px] flex items-center justify-center rounded-full bg-white ring-2 ring-white ring-opacity-25'><Plus color='black' size={12} /></div>
			</div>
		}

		const iconKey = module?.iconUrl;
		const isSelected = isCurrentModuleSelected(module);

		if(iconKey) {
			if (isSelected) {
				return SideMenuFilledIcons?.[module?.iconUrl]?.type ? SideMenuFilledIcons?.[iconKey] : SideMenuIcons?.[iconKey];
			} else {
				return SideMenuIcons?.[iconKey]?.type ? SideMenuIcons?.[iconKey] : SideMenuIcons?.["defaultIcon"];
			}
		}

		return SideMenuIcons?.["defaultIcon"];;
	}

	// Is menu item included in the search or not 
	const isMenuItemIncludedInTheSearchOrNot = (module) => {
		if (expandedModules?.[module?.code]) {
			if (module?.subModules?.some(item => item?.name?.toLowerCase?.()?.includes?.(search?.toLowerCase?.() || ""))) {
				return true;
			}
		}
		// Also implement a check for the submodules as well
		return module?.name?.toLowerCase?.()?.includes(search?.toLowerCase?.() || "");
	}

	// Function : to render the module name
	const renderModuleName = (item) => {
		if (item?.name == "Create-Module") {
			return <div className='flex items-center gap-2'>
				<LanguageTranslator tag='p' className='mb-0 text-xs font-medium'>Create</LanguageTranslator>
				<Sparkles size={13} className='shrink-0' />
			</div>
		}

		return  <LanguageTranslator tag='p' className='mb-0 text-xs font-light'>{item?.name}</LanguageTranslator>
	}

	console.log("checker main menu render")

	// render the sidemenu item
	const renderSidemenuItem = (type, data = [], config) => {

		let {
			expandedMenu = isMenuExpanded?.(),
			hoverMode = true,
			searchEffect = true,
			leftMenu = false
		} = config || {};

		return (
			data?.length > 0 ? <div className={`${type !== 'DASHBOARD' && type !== 'CREATE' ? 'h-full' : ''} ${isMenuExpanded?.() && type === 'REPORTS_ITEM' ? 'scrollbar-thin-dark overflow-y-auto' : ''}`}>
				{data?.map((module, index) => {

					let shouldRenderTooltip;

					if (isCurrentItemIsReportsAndAnalytics?.(module)) {
						shouldRenderTooltip = !expandedMenu && expandMode !== 'reports';
					} else {
						shouldRenderTooltip = !expandedMenu && !(module?.subModules?.length > 0) && expandMode !== 'reports';
					}

					if (module?.isPage == 2) return;

					return (
						(searchEffect ? isMenuItemIncludedInTheSearchOrNot?.(module) : true) && <div className='px-3 mb-1.5 w-auto flex flex-col' key={`menuItem1-${index}`}>
							<div
								// ${expandedModules?.[module?.code] && type !== 'REPORTS_ITEM' ? 'bg-[#3A3A40]' : ''}
								className={`tooltip-target group relative flex items-center gap-2.5 rounded-lg ${type === 'REPORTS_ITEM' ? '' : 'hover:bg-[#3A3A40]'} ${isCurrentModuleSelected(module) ? 'bg-[#3A3A40] 1212' : ''}  ${type !== 'REPORTS_ITEM' ? 'cursor-pointer' : ''} ${expandedMenu ? 'px-[9px] py-2 justify-between' : 'justify-center w-[39px] mx-0'}`}
								onClick={() => {
									if (leftMenu) {
										if (expandMode == "reports") {
											setExpandMode("normal");
											setMenuExpanded(false);
										}
									}
									// (expandMode isCurrentItemIsReportsAndAnalytics?.(module)) || !(module?.subModules?.length > 0)) &&
									handleNavigation(module)
								}}
							>
								{module?.name === 'Reports & Analytics' && !menuExpanded && <Icons.Threedot className='absolute right-[-4px] shrink-0' />}
								<div className={`flex items-center gap-1.5 ${expandedMenu ? '' : 'px-[6px] py-2'}`}>
									<div className='shrink-0'>{renderIcon?.(module)}</div>
									{expandedMenu && renderModuleName(module)}
								</div>

								{/* tooltip */}
								{shouldRenderTooltip && <LanguageTranslator tag='span' className='mainmenu-tooltip'>{module?.name == "Create-Module" ? 'Create' : module?.name}</LanguageTranslator>}

								{
									module?.subModules?.length > 0 && !expandedMenu && !isCurrentItemIsReportsAndAnalytics?.(module) && <Icons.Threedot className='absolute right-[-4px] shrink-0' />
								}

								{expandedMenu && isCurrentItemIsReportsAndAnalytics?.(module) ?
									<ChevronRight color='white' size={13} />
									: expandedMenu && module?.subModules?.length > 0 && expandMode !== 'reports' && <div className='flex justify-end w-full'>
										<div className='shrink-0'>
											{
												expandedModules?.[module?.code] ? <ChevronUp color='white' size={13} /> : <ChevronDown color='white' size={13} />
											}
										</div>
									</div>}

								{/* Render the submodules list here */}
								{/* {Assuming that it has only one child list} */}
								{
									// This part is for the hover 
								  !isCurrentItemIsReportsAndAnalytics?.(module) && !expandedMenu && <div className={`absolute ${expandedMenu ? '' : `pl-[18px] ${index > 5 ? 'bottom-0' : 'top-0'} left-full z-[999] invisible opacity-0 -translate-x-2 pointer-events-none duration-200 group-hover:visible group-hover:opacity-100 group-hover:translate-x-0 group-hover:pointer-events-auto`}`}>
										<div className={`${expandedMenu ? '' : 'overflow-hidden rounded-md shadow-[0px_6px_20px_4px_#0F172A10]'} ${expandMode === 'reports' ? '!shadow-[0px_6px_20px_4px_#212121]' : ''}`}>
											{module?.subModules?.length > 0 && !expandedMenu && <div className={`bg-[#464646] min-w-[130px]`}>
												{/* Render the heading */}
												<div className='px-3 pt-[8px] pb-1.5 border-b border-[#787777]'>
													<p className='mb-0 text-[11px] font-semibold text-white flex items-center gap-2'><LanguageTranslator>{module?.name == "Create-Module" ? 'Create' : module?.name}</LanguageTranslator> {module?.name == "Create-Module" ? <Sparkles size={13} className='shrink-0' /> : ''}</p>
												</div>

												{/* Render the list of the sub modules */}
												<div className='py-1 flex flex-col'>
													{
														module?.subModules?.map((subModule, int) => (
															<div key={`menuItem2-${int}`} onClick={() => handleNavigation(subModule)} className='px-3 py-1.5 text-white cursor-pointer hover:bg-[#3A3A40]'>
																<LanguageTranslator tag='p' className='mb-0 text-[11px]'>{subModule?.name}</LanguageTranslator>
															</div>
														))
													}
												</div>
											</div>}
										</div>
									</div>
								}
							</div>
							{/* For the menu expanded render all the items */}
							{expandedMenu && !isCurrentItemIsReportsAndAnalytics?.(module) &&
								// <AnimatePresence initial={false}>
									module?.subModules?.length > 0 && expandedModules?.[module?.code] &&
										// {module?.subModules?.length > 0 &&
										// <motion.div
										<div
											key="dropdown"
											initial={{ height: 0, opacity: 0 }}
											animate={{ height: 'auto', opacity: 1 }}
											exit={{ height: 0, opacity: 0 }}
											transition={{ duration: 0, ease: 'easeInOut' }}
											// transition={{ duration: 0.2, ease: 'easeInOut' }}
											className={`overflow-hidden ${type === 'CREATE' ? 'mb-1' : ''}`}
										>
											<div className='w-full'>
												{/* <p className='mb-0 text-xs font-light'>{module?.name}</p> */}
												{/* Render the list of the sub modules */}
												<div className='mt-[4px] px-3 pt-[4px] relative flex flex-col'>

													{/* vertical line */}
													{<div className={`absolute top-[4px] left-[18px] h-[calc(100%-2px)] w-[1.5px] ${expandMode === 'reports' ? 'bg-[#787777]/50' : 'bg-[#575757]/50'}`}></div>}
													{
														module?.subModules?.map((subModule, int) => (
															isMenuItemIncludedInTheSearchOrNot?.(subModule) && <div key={`menuItem3-${int}`} onClick={() => handleNavigation(subModule)} className={`ml-[17px] mb-1.5 last:mb-0 py-1 px-[10px] rounded-[5px] hover:bg-[#3A3A40] cursor-pointer ${isCurrentModuleSelected(module, subModule, 2) ? 'bg-[#3A3A40]' : ''}`}>
																<LanguageTranslator tag='p' className='mb-0 text-[11px] font-light whitespace-normal'>{subModule?.name}</LanguageTranslator>
															</div>
														))
													}
												</div>
											</div>
										</div>}
										{/* </motion.div>} */}
								{/* // </AnimatePresence>} */}
						</div>
					)
				})}
			</div> : null
		)
	}


	// Function : to handle the changes in the component
	const handleChange = (type, data) => {
		switch (type) {
			case "expandMenu": {
				setMenuExpanded(true);
				setExpandMode("normal")
				break;
			}
			case "openWorkspace": {
				setMenuExpanded(true);
				setExpandMode("workspace");
				break;
			}
			case "chooseWorkspace": {
				if (data?.moduleCode != activeModule) {
					if (["ADMIN", "MDM"]?.includes(data?.moduleCode)) {
						// if(data?.moduleCode === 'ADMIN') {
						// 	setSkipCallAfterModuleSwitch(true);
						// 	setActiveModule('ADMIN')
						// }

						// const updatedModule = modules?.commonModules?.find(item => activeModule === 'MDM' ? item?.moduleCode === 'ADMIN' : item?.moduleCode === 'MDM')

						// setModules(updatedModule);
						
						if(!["ADMIN", "MDM"]?.includes(activeModule)) {
							setLastActiveModuleStates({
								activeModule: activeModule,
								modules: modules,
								currentPath: location.pathname,
								mainMenu: userDetails?.["mainMenu"],
							})
						}
						// setUserDetails((prev)=> ({...prev, current_main_menu_local_config: {...prev?.current_main_menu_local_config, expandMode: 'settings'}}))
						setExpandMode("settings");
						localStorage.setItem("lastActiveModule", activeModule);
					} else {
						// console.log('current console case : else of case "chooseWorkspace"')
						setExpandMode("normal");
					}
					setActiveModule(data?.moduleCode);
					if (data?.pendingUrl) {
						sessionStorage.setItem("pending_admin_navigation", data.pendingUrl);
					} else {
						localStorage.setItem("go_to_first_page", "true")
					}
					setIsHoveringOverAdminMaster(false);
					setMenuExpanded(false);
				}
				break;
			}
			case "expandModule": {
				// Check : If the click is made on the reports and analytics section
				if (isCurrentItemIsReportsAndAnalytics?.(data)) {
					setExpandMode("reports");
					setMenuExpanded(true);
					setActiveSubModule(data?.code);
					// In this case expand all the modules
					// Get the current reports and analytics module
				} else {
					let updatedExpandedModules = { ...expandedModules };
					if (expandedModules?.[data?.code]) {
						delete updatedExpandedModules[data?.code];
					} else {
						updatedExpandedModules[data?.code] = data;
					}
					setExpandedModules(updatedExpandedModules)
				}
				break;
			}
			case "settingWorkspaceClose": {
				if (lastActiveModuleStates?.activeModule) {
					setActiveModule(lastActiveModuleStates?.activeModule);
					localStorage.setItem("go_to_first_page", "true")
					setModules(lastActiveModuleStates?.modules);
					setSkipCallAfterModuleSwitch(true);
					setUserDetails(prev => ({
						...prev,
						"mainMenu": lastActiveModuleStates?.mainMenu,
					}))
					setTimeout(() => {
						history.push(lastActiveModuleStates?.currentPath);
					}, 0)
				}
				setExpandMode("normal");
				setMenuExpanded(false);
				break;
			}
			case "toggleMenuMode": {
				let updatedUsersDetails = null
				setUserDetails(prev => {
					prev = {
						...prev,
						menuMode: prev?.menuMode === 'old' ? 'new' : 'old',
						current_main_menu_local_config: {
							...prev?.current_main_menu_local_config,
							expandMode: false
						}
					}
					updatedUsersDetails = prev;
					return prev;
				})

				let tokenData = parseJwt(sessionStorage.getItem("token"));
				localStorage.setItem(`menuMode-${tokenData?.jti}`, userDetails?.menuMode === 'old' ? 'new' : 'old')
				localStorage.setItem('refresh_all_tabs', userDetails?.menuMode === 'old' ? `new_${tokenData?.jti}` : `old_${tokenData?.jti}`);
				// Optional cleanup to prevent it from triggering again
				setTimeout(() => {
					localStorage.removeItem('refresh_all_tabs');
				}, 1000);
				setExpandMode(false);
				setMenuExpanded(false);
				setExpandedModules({})
				getDashboardPath(userDetails, history, expandMode, setMenuExpanded, true, updatedUsersDetails?.menuMode)
				break;
			}
			case "goToDashboard": {
				// get the first path for the dashboard
				getDashboardPath(userDetails, history, expandMode, setMenuExpanded, false, userDetails.menuMode)
				break;
			}

			default:
		}
	}

	// Function : To handle the navigation for the component
	const handleNavigation = (module) => {
		if (module?.subModules?.length > 0) {
			// A parent module (e.g. "Manage") owns children but its own pageUrl is
			// only a non-navigable prefix (/app/product/digiproc/manage). In the
			// reports left rail these children are revealed on hover, so a click on
			// the parent must never fall through to history.push — that lands on
			// NoPageFound (404). Expand in the normal menu; no-op in reports mode.
			if (expandMode !== 'reports') {
				handleChange("expandModule", module)
			}
			return;
		} else if (module?.pageUrl) {
			// When the user clicks an Admin sub-item from the hover overlay, the
			// active module is still the previous workspace (e.g. DIGIPROC), so the
			// Admin route is not loaded and history.push lands on NoPageFound.
			// Switch to ADMIN first and defer the actual navigation until the
			// admin routes are mounted.
			if (isHoveringOverAdminMaster && activeModule !== "ADMIN" && activeModule !== "MDM") {
				const overlayModules = modules?.commonModules?.find(item => item?.moduleCode == overlayActiveModule);
				if (overlayModules) {
					handleChange("chooseWorkspace", { ...overlayModules, pendingUrl: module?.pageUrl?.replace(/^#/, '') });
					return;
				}
			}

			// Same class of bug from the Reports & Analytics panel: the report
			// pages shown belong to the active workspace, but the reports left
			// rail (Create / Manage / Dashboard) can point at a page owned by a
			// different workspace module. AppStack only mounts routes for the
			// active module, so a direct history.push lands on NoPageFound (404).
			// Switch to the owning workspace first and defer navigation via
			// pendingUrl so its routes mount before we push.
			const targetModuleCode = resolveWorkspaceSwitchTarget(module?.pageUrl, activeModule);
			if (targetModuleCode) {
				const targetWorkspace = modules?.availableModules?.find(item => item?.moduleCode == targetModuleCode)
					|| modules?.commonModules?.find(item => item?.moduleCode == targetModuleCode);
				if (targetWorkspace) {
					handleChange("chooseWorkspace", { ...targetWorkspace, pendingUrl: module?.pageUrl?.replace(/^#/, '') });
					return;
				}
			}

			openPage(module)();
		}
	}

	// Expose a global function so components outside MainMenu (e.g. UploadExcel)
	// can trigger a proper workspace switch via handleChange("chooseWorkspace", ...).
	// Uses a ref so the window function always delegates to the latest closure.
	const switchWorkspaceRef = useRef(null);
	switchWorkspaceRef.current = (moduleCode, pendingUrl) => {
		const targetWorkspace = modules?.commonModules?.find(item => item?.moduleCode === moduleCode)
			|| modules?.availableModules?.find(item => item?.moduleCode === moduleCode);
		if (targetWorkspace) {
			handleChange("chooseWorkspace", { ...targetWorkspace, pendingUrl });
		}
	};
	useEffect(() => {
		window.switchWorkspaceModule = (moduleCode, pendingUrl) => {
			switchWorkspaceRef.current?.(moduleCode, pendingUrl);
		};
		return () => { delete window.switchWorkspaceModule; };
	}, []);

	const openPage = module => e => {
		const cleanPageUrl = module?.pageUrl?.replace(/^#/, ''); // Remove '#' from the beginning
		if (expandMode == "normal") {
			setMenuExpanded(false);
		}
		history.push(cleanPageUrl);
	};

	const getOtherModuleDescription = {
		'DIGIPROC': 'Simplify procurement with Digiproc! Make ordering smoother, lower risks, and ensure total transparency in your processes.',
		'ALLOKATOR': 'Optimize resources with Allokator! Allocate smarter, cut down waste, and stay in control of your operations effortlessly.',
		'DIGIARS': 'Optimize resources with Allokator! Allocate smarter, cut down waste, and stay in control of your operations effortlessly.',
		'DIGISALES': 'Accelerate your B2B sales with Digisales! Enable lightning-fast orders, eliminate costly errors, and achieve unmatched visibility.'
	}

	const handleInactiveModuleClick = (item) => {

		const moduleLinks = {
			"DIGIARS" : "https://www.supplymint.com/products/allokator",
			"DIGIPROC" : "https://www.supplymint.com/products/digiproc",
			"DIGISALES" : "https://www.supplymint.com/products/digisales",
			// "DIGIVEND" : "",
			// "CDIGISALES" : "",
		}		

		window.open(moduleLinks[item?.moduleCode], '_blank', 'noopener,noreferrer')
	}

	let currentWorkspace = modules?.availableModules?.find(item => item?.moduleCode == activeModule);

	const getModuleBg = {
		'DIGIPROC': 'bg-[#3A3A40]',
		'DIGIARS': 'bg-[#3A403E]',
		'ALLOKATOR': 'bg-[#3A403E]',
		'DIGISALES': 'bg-[#4A4742]'
	}

	// Function  :to render the workspace list and the bottom items
	const renderWorkspace = () => {


		return <div className='pt-[12px] border-t border-[#373737] flex flex-col justify-between h-full text-white'>

			<div>
				{/* Render the Your application tag */}
				<div className='px-3 mb-3.5'>
					<div className='flex items-center gap-1.5'>
						<div className='p-[4px] h-[32px] w-[32px] flex items-center justify-center rounded-lg bg-[#FFFFFF1A]'><Icons.ApplicationsSemiFill /></div>
						<LanguageTranslator tag='p' className='!mb-0 text-sm font-medium'>Your Applications</LanguageTranslator>
					</div>
				</div>

				{/* Render the workspace items here */}
				<div className='px-3 space-y-2.5'>
					{
						modules?.["availableModules"]?.map((item, int) => (
							<div key={`menuItem4-${int}`} className={`px-[11px] py-2 w-full flex items-center gap-1.5 rounded-lg ring-[1px] cursor-pointer duration-100 hover:ring-[#FFFFFF99] ${getModuleBg[item?.moduleCode?.toUpperCase()]} ${currentWorkspace?.["moduleName"]?.toLowerCase() === item?.moduleName?.toLowerCase() ? 'ring-[#FFFFFF99]' : 'ring-transparent'}`} onClick={() => handleChange("chooseWorkspace", item)}>
								<div className={item?.iconUrl ? `max-w-[104px] h-[33px] flex items-center` : 'h-[32px] w-[32px] p-[3px] shrink-0'}>{getWorkspaceIcon?.(item, { isWorkspaceIcon: true, skeletonClassName: 'top-[-12px] left-[-12px] !min-h-[51px] !min-w-[210px]' })}</div>
								{!item?.iconUrl && <LanguageTranslator tag='p' className='mb-0 text-base font-semibold'>{item?.moduleName || ""}</LanguageTranslator>}
							</div>
						))
					}
				</div>

			</div>

			<div>
				{/* render the master data and administration */}
				<div className='mb-4 px-3 space-y-4'>
					{
						modules?.["commonModules"]?.map((item, int) => (
							<div key={`menuItem5-${int}`} className='pr-2.5 flex items-center gap-1.5 rounded-lg hover:bg-[#3A3A40] cursor-pointer' onClick={() => {
								if(isHoveringOverAdminMaster){
									setOverlayActiveModule(item?.moduleCode)
								} else {	
									handleChange("chooseWorkspace", item) }}
								}
								>
								<div className='p-[6px] shrink-0 bg-[#3A3A40] rounded-md h-[36px] w-[36px] flex items-center justify-center'>{getWorkspaceIcon?.(item, { skeletonClassName: 'top-[-20px] left-[-20px] !min-h-[40px] !min-w-[209px]' })}</div>
								<LanguageTranslator tag='p' className='mb-0 text-xs font-light'>{item?.moduleName || ""}</LanguageTranslator>
							</div>
						))
					}
				</div>

				{modules?.inactiveModules?.length > 0 &&
					<div className='px-3 pt-[16px] pb-5 border-t border-[#373737]'>
						<div
							style={{
								backgroundImage: `url(${ImgPath.BgRectangle})`,
								backgroundRepeat: 'no-repeat',
								backgroundSize: '150%',
								backgroundPosition: 'center'
							}}
							className='px-3 py-2.5 rounded-[10px]'
						>

							<div className='mb-4 flex items-center gap-1.5'>
								<div className='shrink-0'><Compass size={14} /></div>
								<LanguageTranslator tag='p' className='mb-0 text-xs'>Explore Supplymint</LanguageTranslator>
							</div>
							{
								modules?.inactiveModules?.map((item, int) => (
									<Tooltip
										key={`menuItem6-${int}`}
										placement='right'
										motion={false}
										trigger="hover"
										transitionName=""
										mouseEnterDelay={0}
										// mouseLeaveDelay={0}
										overlayClassName="!p-0 !bg-transparent !shadow-none !rounded-none"
										overlayInnerStyle={{
											padding: 0,
											paddingLeft: 20,
											background: 'transparent',
											boxShadow: 'none',
											borderRadius: 0,
										}}
										title={<div
											className='p-[12px] relative w-[214px] rounded-[10px] bg-[#212121] text-white'
											style={{
												backgroundImage: `url(${ImgPath.BgRectangle})`,
												backgroundRepeat: 'no-repeat',
												backgroundSize: 'cover',
												backgroundPosition: 'center',
												backgroundSize: '550%',
												boxShadow: '0px 4px 21px -2px #3F3E3E26'
											}}
										>
											<div className='mb-3 flex items-center gap-1.5'>
												<div className='max-h-[40px]'>{getWorkspaceIcon?.(item, { isWorkspaceIcon: true })}</div>
												{!item?.iconUrl && <LanguageTranslator tag='p' className='!mb-0 text-base font-semibold'>{item?.moduleName || ""}</LanguageTranslator>}
											</div>
											<LanguageTranslator tag='p' className='mb-0 text-[11px] font-light'>{getOtherModuleDescription[item?.moduleCode] || ''}</LanguageTranslator>
										</div>}
									>
										<div className='mb-3.5 last:mb-1.5 group relative flex items-center gap-4 justify-between cursor-pointer hover:opacity-75'>
											<div onClick={()=>handleInactiveModuleClick(item)} className='flex items-center gap-1.5'>
												{getWorkspaceIcon?.(item, { isWorkspaceIcon: true })}
												{!item?.iconUrl && <LanguageTranslator tag='p' className='!mb-0 text-base font-semibold'>{item?.moduleName || ""}</LanguageTranslator>}
											</div>
											<ChevronRight size={14} color='#CCCCCC' className='shrink-0 duration-200 invisible opacity-0 -translate-x-[4px] pointer-events-none group-hover:visible group-hover:opacity-100 group-hover:translate-x-0 group-hover:pointer-events-auto' />
										</div>
									</Tooltip>
								))
							}
						</div>
					</div>
				}
			</div>

		</div>
	}

	// Function : To render the icon for the workspace.
	const getWorkspaceIcon = (current, config = {}) => {

		const { isWorkspaceIcon = false, skeletonClassName = '', className = '' } = config;

		let LOGO = {
			"VENDOR": Icons.VendorSupplymintLogo,
			"CUSTOMER": Icons.CustomerSupplymintLogo,
			"ENT": Icons.NewSupplymintLogo
		}

		let logo = <div className='h-[42px] mx-[0]'><img className={`h-full w-full object-contain object-center ${uType == "ENT"?.toUpperCase() ? "" : "snl-img"}`} onClick={() => handleChange("openWorkspace")}
			src={LOGO?.[uType?.toUpperCase()] || Icons.NewSupplymintLogo} alt='Supplymint Auth Logo Image'
		/></div>

		return (
			<div className={`max-w-[122px] shrink-0 ${isWorkspaceIcon ? 'h-[25px]' : 'h-auto'} ${className}`}>
				{/* {true ? */}
				<SuspenseImage skeletonClassName={skeletonClassName} className={`${(isWorkspaceIcon || (isMenuExpanded?.() && expandMode !== 'reports')) ? 'shrink-0 h-full w-full object-contain object-center' : 'h-[34px] aspect-square object-cover object-left'}`} src={isWorkspaceIcon ? current?.workspaceUrl : current?.iconUrl} loading="lazy" />
			</div>
		)

		return logo
	}

	// Function : To check if the current module is settings
	const isCurrentModuleSettings = () => {
		if (["ADMIN", "MDM"]?.includes(activeModule)) {
			return true;
		}
		return false;
	}

	const getCurrenrtActiveSubModuleData = () => {
		let activeSubModuleData = modules?.moduleList?.[0]?.[0]?.subModules?.find(item => item?.code == activeSubModule);

		return activeSubModuleData;
	}

	const getCurrentWorkspaceItem = () => {
		let currentWorkspace = isCurrentModuleSettings?.() ? lastActiveModuleStates ? lastActiveModuleStates?.modules?.availableModules?.find(item => item?.moduleName == lastActiveModuleStates?.activeModule) : null : modules?.availableModules?.find(item => item?.moduleName == activeModule);
		return currentWorkspace;
	}

	const isCurrentModuleSelected = (item, subModule, level = 1) => {
		let path = location?.pathname;
		if (level == 1) {
			if (expandMode === 'reports' && (item?.code === 'RDREPOMAIN' || item?.code === 'VDREPOMAIN')) {
				return true;
			} else if (item === 'dashboard') {
				return path.includes('dashboard');
			}
		}

		return level > 1 ? subModule?.["pageUrl"]?.includes(path) && modulesJson[item?.code]?.[`#${path}`] : modulesJson[item?.code]?.[`#${path}`]
	}

	const renderMenu = () => {
		const getModulesModified = (current = modules?.["moduleList"]?.[0]?.[0], level = 0) => {
			let moduleList = [];

			// adminModules always holds ADMIN data (the request hardcodes
			// moduleCode='ADMIN'), so only use it for the Admin & Master hover
			// preview. In settings mode, the current workspace's mainMenu
			// already has the right items (ADMIN items when active=ADMIN,
			// MDM items when active=MDM).
			((isHoveringOverAdminMaster) ? overlayModules[overlayActiveModule]?.moduleList?.[0]?.[0] : current)?.subModules?.map(item => {
				moduleList.push(item);

				if (item?.subModules?.length > 0) {
					let modifiedModules = getModulesModified?.(item, level + 1)
					if (modifiedModules?.moduleList?.length > 0) {
						moduleList.subModules = modifiedModules?.moduleList || [];
					}
				}
			});

			return {
				moduleList
			};

		}

		let modifiedModules = getModulesModified?.()
		const rendeWorkspaceIcon = () => {
			let currentWorkspace = getCurrentWorkspaceItem?.()
			return (
				// Render the workspace icon
				<div className={`py-3 pl-4 pr-2 h-[56px] flex justify-between items-center gap-3 ${isMenuExpanded?.() ? 'bg-[#212121]' : 'relative'}`}>
					<div className='flex items-center gap-2'>
						{/* render the icon */}
						{getWorkspaceIcon?.(currentWorkspace, { skeletonClassName: isMenuExpanded?.() && 'top-[-16px] left-[0px] !min-h-[34px] !min-w-[150px]' })}
						{/* If the menu is expanded then render the workspace name  */}
						{isMenuExpanded?.() && !currentWorkspace?.iconUrl && <LanguageTranslator tag='p' className='!mb-0 text-lg font-semibold'>{currentWorkspace?.["moduleName"] || ""}</LanguageTranslator>}
					</div>
					{renderExpandViewSwitcher?.()}
				</div>
			)

		}

		// Function : To provide the create modules
		const provideCreateModules = () => {
			if (modules?.createModuleList?.length > 0) {
				// Style Bazaar (schemaEntID 385): hide "Indent" and "Order" from the Create menu
				const restrictedSchemaEntId = ["385"];
				const isCreateRestricted = restrictedSchemaEntId.includes(parseJwt(sessionStorage.getItem("token"))?.schemaEntID);
				const filteredCreateModuleList = isCreateRestricted
					? modules?.createModuleList?.filter(item => !/(createIndent|indents\/create|createOrder|orders\/create)/i.test(item?.page_url || ""))
					: modules?.createModuleList;

				if (!(filteredCreateModuleList?.length > 0)) return null;

				let createModules = [{
					code: "CREATE",
					crud: "1111",
					name: "Create-Module",
					icon_url: "create-icon.svg",
					subModules: filteredCreateModuleList?.map(item => ({ ...item, "name": item?.module_name || "", pageUrl: item?.page_url })) || [],
				}]

				return createModules;
			}
			return null;
		}

		// Function : To render the components
		const renderComponents = (mode = expandMode) => {

			switch (mode) {
				case "settings":
				case "normal": {

					const currentModule = modules?.commonModules?.find(item => item?.moduleCode == activeModule)?.moduleCode;

					return <React.Fragment>

						{(!isHoveringOverAdminMaster && expandMode !== 'settings') && renderSidemenuItem("CREATE", provideCreateModules?.())}

						{/* Render the dashboard icon here */}
						<div className={`${isMenuExpanded?.() ? 'h-full scrollbar-thin-dark overflow-y-auto overflow-x-hidden flex flex-col' : 'h-[calc(100%-147px)]'}`}>
							{!isHoveringOverAdminMaster && expandMode !== 'settings' && !isCurrentModuleSettings?.() && isMenuItemIncludedInTheSearchOrNot?.({ name: "Dashboard" }) && <div onClick={() => handleChange("goToDashboard")} className='px-3 mb-1.5 w-auto flex flex-col' key={module?.id}>
								<div
									className={`tooltip-target cursor-pointer flex items-center gap-1.5 rounded-lg hover:bg-[#3A3A40] ${isCurrentModuleSelected('dashboard') ? 'bg-[#3A3A40]' : ''} ${isMenuExpanded?.() ? 'px-[9px] py-2' : 'justify-center w-[39px] mx-0 px-[6px]'}`}
									onClick={() => {
										const currentPath = location?.pathname || '';

										if (!currentPath.includes('dashboard')) {
											history.push('/app');
										}
									}}
								>
									<div className={`shrink-0 ${isMenuExpanded?.() ? '' : 'px-[6px] py-2'}`}>{isCurrentModuleSelected('dashboard') ? <Icons.DashboardMenuFilledV2 height={19} /> : <Icons.DashboardMenuV2 height={19} />}</div>
									{isMenuExpanded?.() && <LanguageTranslator tag='p' className='mb-0 text-xs font-light'>Dashboard</LanguageTranslator>}
									{!isMenuExpanded?.() && <LanguageTranslator tag='span' className='mainmenu-tooltip'>Dashboard</LanguageTranslator>}
								</div>
							</div>}
							<div
								ref={containerRef}
								className={`h-full relative ${isMenuExpanded?.() ? "" : "overflow-y-clip"}`}
								onMouseMove={handleMouseMove}
								onMouseLeave={handleMouseLeave}
							>
								<div
									ref={moduleContentRef}
									className={`${isMenuExpanded?.() ? '' : 'pt-[3px] absolute transition-[top] duration-500 ease-out'}`}
									// style={{ 
									// 	transform: `translateY(-${Math.round(topValue)}px)`,
									//  }}
									style={{
										top: isMenuExpanded?.() ? '' : `-${Math.round(topValue)}px`,
									}}
								// style={{
								// 	top: `-${topValue}px`,
								// 	transition: 'top .5s ease-out'
								// }}
								>
									{renderSidemenuItem("DASHBOARD", modifiedModules?.moduleList)}
								</div>
							</div>
						</div>

						{(isHoveringOverAdminMaster || expandMode === 'settings') && <div className='mb-4 px-3 space-y-4 pt-[12px] border-t border-[#373737]'>
							{
							modules?.["commonModules"]?.map((item, int) => (
									<div key={`menuItem5-${int}`} className={`pr-2.5 flex items-center gap-1.5 rounded-lg hover:bg-[#3A3A40] cursor-pointer ${currentModule == item?.moduleCode ? 'bg-[#3A3A40]' : ''}`} onClick={() => {
								if(isHoveringOverAdminMaster){
									setOverlayActiveModule(item?.moduleCode)
								} else {	
									handleChange("chooseWorkspace", item) }}
								}>
										<div className={`p-[6px] shrink-0 rounded-md h-[36px] w-[36px] flex items-center justify-center ${currentModule == item?.moduleCode ? 'bg-[#3A3A40]' : 'bg-[#3A3A40]'}`}>{getWorkspaceIcon?.(item, { isWorkspaceIcon: currentModule == item?.moduleCode ? true : false, className: currentModule === item?.moduleCode ? '!h-auto' : '', skeletonClassName: 'top-[-20px] left-[-20px] !min-h-[40px] !min-w-[209px]' })}</div>
										<LanguageTranslator tag='p' className='mb-0 text-xs font-light w-full'>{item?.moduleName || ""}</LanguageTranslator>
										{(currentModule == item?.moduleCode || (isHoveringOverAdminMaster && overlayActiveModule == item?.moduleCode)) && <CheckCheck size={15} color='white' className='shrink-0 mr-1' />}
									</div>
								))
							}
						</div>}

					</React.Fragment>
				}
				case "reports": {
					if (activeSubModule) {

						// Find the active submodule and with the active submodule get the current reports and anaytics section

						let reportsAndAnalyticsModule = getCurrenrtActiveSubModuleData?.();

						return renderSidemenuItem("REPORTS_ITEM", reportsAndAnalyticsModule?.subModules?.filter(item => item?.subModules?.length > 0))

					}

					return <div>

					</div>
				}
				default: {
					// console.log('runnnning')
					return <React.Fragment>

						{/* Render the workspace here */}
						{renderWorkspace()}

					</React.Fragment>
				}
			}
		}

		// Function  :To check , should we render the sarch box or not
		const shouldRenderSearchBox = () => {
			return isHoveringOverAdminMaster || ["MDM", "ADMIN"]?.includes(activeModule) || expandMode == "reports";
		}

		// Function : to render the search
		const renderSearchBox = () => {
			return shouldRenderSearchBox?.() && <div className='px-3 pt-[12px]'>
				<GenericInput
					genInputExtraClass='menuSearchInput transparent-input'
					type="text"
					value={search}
					onChange={(e) => setSearch(e?.target?.value || "")}
					inputKey="menuSearch"
					dependentFieldsObject={["menuSearch"]}
					placeholder={"Type To Search"}
					iconLeft={<Icons.NewSearchIconWhite width={17} height={17} className='shrink-0 mr-[4px]' />}
					showTooltipLenght={17}
				/>
			</div>
		}

		const renderSettingWorkspaceHeading = () => {
			let currentSettingWorkspace = modules?.commonModules?.find(item => isHoveringOverAdminMaster ? item?.moduleCode === overlayActiveModule : item?.moduleCode == activeModule)
			if (isHoveringOverAdminMaster || isCurrentModuleSettings?.()) {
				return <div className='pl-3 pr-2.5 flex items-center gap-1.5 rounded-lg'>
					<div className='p-[6px] shrink-0 bg-[#3A3A40] rounded-md h-[36px] w-[36px] flex items-center justify-center'>{getWorkspaceIcon?.(currentSettingWorkspace, { isWorkspaceIcon: true, className: '!h-auto' })}</div>
					<LanguageTranslator tag='p' className='mb-0 text-xs font-light'>{currentSettingWorkspace?.moduleName || ""}</LanguageTranslator>
				</div>
			}

			if (expandMode == "reports") {
				let reportsAndAnalyticsModule = getCurrenrtActiveSubModuleData?.()
				return <div className='pr-3 pt-[16px] flex justify-between items-center gap-3'>
					<div className='pl-3 pr-2.5 flex items-center gap-2 rounded-lg'>
						<div className='p-[6px] shrink-0 bg-[#3A3A40] rounded-md h-[36px] w-[36px] flex items-center justify-center'>{renderIcon?.(reportsAndAnalyticsModule)}</div>
						<LanguageTranslator tag='p' className='mb-0 text-sm'>{reportsAndAnalyticsModule?.name || ""}</LanguageTranslator>
					</div>
					{renderExpandViewSwitcher?.()}
				</div>
			}
		}

		// Function : To render the side panel arrow to open and close the expand view of the menu
		const renderExpandViewSwitcher = () => {
			return isCurrentModuleSettings?.() ?
				<GenericCustomButton
					// type='default'
					className={'mr-[2px] h-[20px] w-[20px] flex items-center justify-center bg-[#FFFFFF1A] hover:bg-white text-white hover:text-[#111D31] rounded-md'}
					leftIcon={<X size={14} strokeWidth={2.25} />}
					// label="Back"
					onClick={() => {
						handleChange("settingWorkspaceClose")
					}}
				/>
				: (
					expandMode === 'workspace' ? <GenericCustomButton
						// type='default'
						className={'flex items-center gap-1 text-[10px]'}
						leftIcon={<ArrowLeft size={12} />}
						label="Back"
						onClick={() => {
							setExpandMode("normal");
							setMenuExpanded(false);
						}}
					/> : <button
						className={`p-[2px] rounded-md flex items-center justify-center shrink-0 text-white hover:text-[#111D31] cursor-pointer ${isMenuExpanded?.() ? 'bg-[#FFFFFF1A] h-[23px] w-[23px] static hover:bg-white' : 'bg-[#373737] border border-[#FFFFFF1A] h-[20px] w-[20px] absolute top-[19px] left-[55px]'}`}
						onMouseEnter={() => {
							if (isMenuExpanded?.()) {
								// Menu is already expanded, don't close
							}
							 else {
								handleChange("expandMenu")
							}
						}}
						onMouseLeave={() => {
							// Prevent blinking by not collapsing when leaving button if menu is expanded
							// Let the main container's onMouseLeave handle it
						}}
						onClick={()=>{
							if (isMenuExpanded?.()) {
								setExpandMode("normal");
								setMenuExpanded(false);
							}
						}}
					>
						{isMenuExpanded?.() ? <ChevronLeft size={16} /> : <ChevronRight size={12} strokeWidth={2} />}
					</button>
				)

		}

			const renderAdminMasterButton = (config = {}) => {

				let { expandedMenu = isMenuExpanded?.() } = config || {};

				const handleAdminMouseEnter = ()=>{
					setIsHoveringOverAdminMaster(true)
					handleChange("expandMenu")
				}
				// const handleAdminMouseLeave = ()=>{
				// 	setIsHoveringOverAdminMaster(false)
				// 	if (!["ADMIN", "MDM"]?.includes(activeModule) && expandMode !== "reports") {
				// 		setExpandMode("normal");
				// 		setMenuExpanded(false);
				// 	}
				// }

				return <div className='px-2.5 mb-4 w-auto flex flex-col'>
						<div onMouseEnter={handleAdminMouseEnter} className={`tooltip-target group cursor-pointer flex items-center gap-1.5 rounded-lg ${expandedMenu ? 'px-[4px] py-1.5 hover:bg-[#3A3A40]' : 'justify-center w-[44px] mx-0 px-[6px] hover:bg-[#3A3A40]'}`}>
							<div className="p-[5px] rounded-lg shrink-0 bg-[#3A3A40] border border-[#B6BACA] group-hover:bg-[#FFFFFF33]"><Bolt color='white' size={22} /></div>
							{expandedMenu && <LanguageTranslator tag='p' className='mb-0 text-xs font-light'>Admin & Master</LanguageTranslator>}
						</div>
					</div>
			}

			// Function : To check if we should render the workspace icon or not
			const shouldRenderWorkspaceIcon = () => {
				if (expandMode == "reports") return false;
				return true;
			}

			// Function : To render the workspace button
			const renderYourWorkspaceButton = (config = {}) => {
				

				let { expandedMenu = isMenuExpanded?.() } = config || {};
				return (
					<div className='px-2.5 pb-2.5 w-auto flex flex-col'>
						<div className='group relative'>
							<div className={`tooltip-target cursor-pointer flex items-center gap-1.5 rounded-lg ${expandedMenu ? 'px-[4px] py-1.5 hover:bg-[#3A3A40]' : 'p-[4px] justify-center w-[44px] mx-0 px-[6px] hover:bg-[#3A3A40]'}`} >
								<div className="p-[5px] rounded-lg shrink-0 bg-[#3A3A40] border border-[#B6BACA] group-hover:bg-[#FFFFFF33]"><Grip color='white' size={22} /></div>
								{/* onClick={() => handleChange("openWorkspace")} onclick remove from above div to show all the workspace list */}
								{expandedMenu && <LanguageTranslator tag='p' className='mb-0 text-xs font-light'>Your Workspaces</LanguageTranslator>}
								{/* {!expandedMenu && expandMode !== 'reports' && <LanguageTranslator tag='span' className='mainmenu-tooltip'>Your Workspaces</LanguageTranslator>} */}
								
							</div>
							<div className={`${`pl-[18px] absolute bottom-0 left-full z-[999] invisible opacity-0 -translate-x-2 pointer-events-none duration-200 group-hover:visible group-hover:opacity-100 group-hover:translate-x-0 group-hover:pointer-events-auto`}`}>
								<div className={`${'overflow-hidden rounded-md shadow-[0px_6px_20px_4px_#0F172A10]'} ${expandMode === 'reports' ? '!shadow-[0px_6px_20px_4px_#212121]' : ''}`}>
									{modules?.["availableModules"]?.length > 0 && <div className={`p-[1px] bg-[#464646] min-w-[170px]`}>
										{/* Render the heading */}
										<div className='px-3 pt-[8px] pb-1.5 border-b border-[#787777]'>
											<p className='mb-0 text-[11px] font-semibold text-white flex items-center gap-2'><LanguageTranslator>{<Component size={14} />} Your Modules</LanguageTranslator></p>
										</div>

										{/* Render the list of the sub modules */}
										<div className='mt-[8px] mb-[4px] py-1 px-3 space-y-2.5'>
											{
												modules?.["availableModules"]?.map((item, int) => (
													// <div key={`menuItem2-${int}`} onClick={() => handleNavigation(subModule)} className='px-3 py-1.5 text-white cursor-pointer hover:bg-[#3A3A40]'>
													// 	<LanguageTranslator tag='p' className='mb-0 text-[11px]'>{subModule?.name}</LanguageTranslator>
													// </div>
													// <div key={`menuItem4-${int}`} className={`px-[11px] py-2 w-full flex items-center gap-1.5 rounded-lg ring-[1px] cursor-pointer duration-100 hover:ring-[#FFFFFF99] ${getModuleBg[item?.moduleCode?.toUpperCase()]} ${currentWorkspace?.["moduleName"]?.toLowerCase() === item?.moduleName?.toLowerCase() ? 'ring-[#FFFFFF99]' : 'ring-transparent'}`} onClick={() => handleChange("chooseWorkspace", item)}>
														<div className={`flex items-center justify-between gap-7 min-w-[140px] ${item?.moduleCode !== activeModule ? 'cursor-pointer hover:opacity-75' : ''}`} onClick={() => handleChange("chooseWorkspace", item)}>
															<div key={`menuItem4-${int}`} className={item?.iconUrl ? `shrink-0 max-w-[104px] h-[33px] flex items-center` : 'h-[32px] w-[32px] p-[3px] shrink-0'}>{getWorkspaceIcon?.(item, { isWorkspaceIcon: true, className: 'max-w-[97px]', skeletonClassName: 'top-[-12px] left-[-12px] !min-h-[51px] !min-w-[210px]' })}</div>
															{currentWorkspace?.["moduleName"]?.toLowerCase() === item?.moduleName?.toLowerCase() && <CheckCheck color='white' size={13} className='shrink-0' />}
														</div>
														// {!item?.iconUrl && <LanguageTranslator tag='p' className='mb-0 text-base font-semibold'>{item?.moduleName || ""}</LanguageTranslator>}
													// </div>
												))
											}
										</div>

										{/* Render the list of inactive */}
										{modules?.inactiveModules?.length > 0 && <div
											// style={{
											// 	backgroundImage: `url(${ImgPath.BgRectangle})`,
											// 	backgroundRepeat: 'no-repeat',
											// 	backgroundSize: '150%',
											// 	backgroundPosition: 'center'
											// }}
											className='mt-[20px] m-[4px] px-3 py-2.5 rounded-[5px] bg-[#717171]'
										>

											{/* <div className='mb-4 flex items-center gap-1.5'>
												<div className='shrink-0'><Compass size={14} /></div>
												<LanguageTranslator tag='p' className='mb-0 text-xs'>Explore Supplymint</LanguageTranslator>
											</div> */}
											{
												modules?.inactiveModules?.map((item, int) => (
													// <Tooltip
													// 	key={`menuItem6-${int}`}
													// 	placement='right'
													// 	motion={false}
													// 	trigger="hover"
													// 	transitionName=""
													// 	mouseEnterDelay={0}
													// 	// mouseLeaveDelay={0}
													// 	overlayClassName="!p-0 !bg-transparent !shadow-none !rounded-none"
													// 	overlayInnerStyle={{
													// 		padding: 0,
													// 		paddingLeft: 20,
													// 		background: 'transparent',
													// 		boxShadow: 'none',
													// 		borderRadius: 0,
													// 	}}
													// 	title={<div
													// 		className='p-[12px] relative w-[214px] rounded-[10px] bg-[#212121] text-white'
													// 		style={{
													// 			backgroundImage: `url(${ImgPath.BgRectangle})`,
													// 			backgroundRepeat: 'no-repeat',
													// 			backgroundSize: 'cover',
													// 			backgroundPosition: 'center',
													// 			backgroundSize: '550%',
													// 			boxShadow: '0px 4px 21px -2px #3F3E3E26'
													// 		}}
													// 	>
													// 		<div className='mb-3 flex items-center gap-1.5'>
													// 			<div className='max-h-[40px]'>{getWorkspaceIcon?.(item, { isWorkspaceIcon: true })}</div>
													// 			{!item?.iconUrl && <LanguageTranslator tag='p' className='!mb-0 text-base font-semibold'>{item?.moduleName || ""}</LanguageTranslator>}
													// 		</div>
													// 		<LanguageTranslator tag='p' className='mb-0 text-[11px] font-light'>{getOtherModuleDescription[item?.moduleCode] || ''}</LanguageTranslator>
													// 	</div>}
													// >
														<div className='mb-3.5 last:mb-0 relative flex items-center gap-4 justify-between'>
															<div onClick={()=>handleInactiveModuleClick(item)} className='moduleLogo flex items-center gap-1.5 w-full cursor-pointer'>
																{getWorkspaceIcon?.(item, { isWorkspaceIcon: true, className: 'max-w-[80px] !h-[20px]' })}
																{!item?.iconUrl && <LanguageTranslator tag='p' className='!mb-0 text-base font-semibold'>{item?.moduleName || ""}</LanguageTranslator>}
																<div className='link-icon hidden w-full items-center justify-end'>
																	<ExternalLink size={14} className='shrink-0' />
																</div>
															</div>
															{/* <ChevronRight size={14} color='#CCCCCC' className='shrink-0 duration-200 invisible opacity-0 -translate-x-[4px] pointer-events-none group-hover:visible group-hover:opacity-100 group-hover:translate-x-0 group-hover:pointer-events-auto' /> */}
														</div>
													// {/* </Tooltip> */}
												))
											}
										</div>}
									</div>}
								</div>
							</div>
						</div>
					</div>
				)
			}

			// Function : to render the left icons in case of reports mode
			const renderLeftIconsForReportsMode = () => {
				let currentWorkspace = getCurrentWorkspaceItem?.()
				return (
				<div>

					{/* Render the workspace icon */}
					<div className='flex flex-col justify-between h-full'>
						<div className='h-full overflow-y-clip'>
							{/* render the icon */}
							<div className='py-3 pl-4 pr-2 min-h-[56px]'>
								{getWorkspaceIcon?.(currentWorkspace)}
							</div>

							{!isHoveringOverAdminMaster && expandMode !== 'settings' && renderSidemenuItem("CREATE", provideCreateModules?.(), {
								expandedMenu: false,
								hoverMode: false,
								searchEffect: false,
								leftMenu: true,
							})}
							{/* Render the dashboard icon here */}
							{!isHoveringOverAdminMaster && expandMode !== 'settings' && !isCurrentModuleSettings?.() && <div onClick={() => {
								handleChange("goToDashboard")
								if (expandMode == "reports") {
									setExpandMode("normal");
									setMenuExpanded(false);
								}
							}} className='px-3 mb-1.5 w-auto flex flex-col' key={module?.id}>
								<div className={`tooltip-target cursor-pointer flex items-center gap-1.5 rounded-lg hover:bg-[#3A3A40] ${isMenuExpanded?.() ? 'px-[9px] py-2' : 'justify-center w-[39px] mx-auto px-[6px]'}`}>
									<div className={`shrink-0 ${isMenuExpanded?.() ? '' : 'px-[6px] py-2'}`}><Icons.DashboardMenuV2 /></div>
								</div>
							</div>}
							<div
								ref={containerRef}
								className={`h-[calc(100%-146px)] relative overflow-y-clip`}
								onMouseMove={handleMouseMove}
								onMouseLeave={handleMouseLeave}
							>
								<div
									ref={moduleContentRef}
									className={`pt-[6px] pb-[4px] absolute transition-[top] duration-500 ease-out`}
									style={{
										top: `-${Math.round(topValue)}px`,
									}}
								>

									{renderSidemenuItem("DASHBOARD", modifiedModules?.moduleList, {
										expandedMenu: false,
										hoverMode: false,
										searchEffect: false,
										leftMenu: true,
									})}
								</div>
							</div>
						</div>

						<div>

							{/* {renderUpgradePopup?.()} */}

							{tokenData?.newModuleStructure != 1 && <div className={`group relative my-2 px-1 ${uType !== 'ENT' ? 'mb-[50px]' : ''}`}>
								<div
									style={{
										backgroundImage: `url(${ImgPath.BgRectangle})`,
										backgroundRepeat: 'no-repeat',
										backgroundSize: 'cover',
										backgroundPosition: 'center',
										backgroundSize: '310%',
										boxShadow: '0px 4px 21px -2px #3F3E3E26'
									}}
									className={`bg-[#0202021A] flex items-center rounded-[10px] px-0 py-2 flex-col gap-2`}
								>
									<p className={`mb-0 whitespace-normal flex leading-[1.1] text-[10px] text-center flex-col text-white`}><LanguageTranslator tag='span'>New</LanguageTranslator></p>
									<Switch
										className="sidemenu-switch"
										style={{
											width: 30,
											backgroundColor: 'white'
										}}
										checked={userDetails?.menuMode === 'new'}
										onChange={() => handleChange("toggleMenuMode")}
									/>

								</div>
							</div>}

							{/* Render the workspace icon */}
							{uType === 'ENT' && !isHoveringOverAdminMaster &&  renderYourWorkspaceButton({
								expandedMenu: false,
							})}

							{/* Render the admin master button */}
							{uType === 'ENT' && !isHoveringOverAdminMaster &&
								renderAdminMasterButton({
								expandedMenu: false,
							})
							}

						</div>
					</div>

				</div>
			)
		}

		const tokenData = parseJwt?.(sessionStorage.getItem("token"));

		return (

			<React.Fragment>

				<div className='flex h-full w-full'>

					{/* Render the left menu for the menu items so that we should render the icons also in case of the reports */}
					{expandMode == "reports" && renderLeftIconsForReportsMode()}

					<div className={`flex flex-col justify-between h-full w-full text-white`}>

						<React.Fragment>

							<div  className={`${(expandMode === 'workspace' || expandMode === 'settings' || expandMode == "reports") ? 'h-full flex flex-col justify-between' : ''} ${expandMode === 'reports' ? 'bg-[#464646]' : ''} ${isMenuExpanded?.() ? 'scrollbar-thin-dark flex flex-col h-full overflow-y-auto overflow-x-hidden' : 'h-[calc(100%-137px)]'}`}>

								{/* Render the workspace icon  */}
								{shouldRenderWorkspaceIcon?.() && rendeWorkspaceIcon()}

								{/* Render the setting workspace heading */}
								{renderSettingWorkspaceHeading?.()}

								{/* Render the search box  */}
								{renderSearchBox()}

								{/* To render the menu forn the current active module */}

								{

									renderComponents()

								}

							</div>

						</React.Fragment>

						{!isHoveringOverAdminMaster && !isCurrentModuleSettings?.() && <React.Fragment>

							<div className='pb-3 flex flex-col justify-end'>
								{/* {renderUpgradePopup?.()} */}

								{!isHoveringOverAdminMaster && expandMode != "reports" && tokenData?.newModuleStructure != 1 && <div className={`group relative my-2 ${isMenuExpanded?.() ? 'px-3' : 'px-1'} ${uType !== 'ENT' && tokenData?.uType?.toUpperCase() !== 'VENDOR' ? 'mb-[50px]' : ''}`}>
									<div
										style={{
											backgroundImage: `url(${ImgPath.BgRectangle})`,
											backgroundRepeat: 'no-repeat',
											backgroundSize: 'cover',
											backgroundPosition: 'center',
											backgroundSize: isMenuExpanded?.() ? '130%' : '310%',
											boxShadow: '0px 4px 21px -2px #3F3E3E26'
										}}
										className={`bg-[#0202021A] flex items-center rounded-[10px] ${isMenuExpanded?.() ? 'px-3 py-3 flex-row justify-between gap-3' : 'px-0 py-2 flex-col gap-2'}`}
									>
										<p className={`mb-0 whitespace-normal flex leading-[1.1] ${isMenuExpanded?.() ? 'text-xs text-left flex-row gap-1' : 'text-[10px] text-center flex-col'}`}><LanguageTranslator tag='span'>New</LanguageTranslator> </p>
										<Switch
											className="sidemenu-switch"
											style={{
												width: 30,
												backgroundColor: 'white'
											}}
											checked={userDetails?.menuMode === 'new'}
											onChange={() => handleChange("toggleMenuMode")}
										/>

									</div>

									{/* tooltip */}
									{!menuExpanded && <div className='pl-[16px] absolute left-full bottom-[-30px] z-[2] invisible opacity-0 pointer-events-none group-hover:visible group-hover:opacity-100 group-hover:pointer-events-auto'>
										<div
											className='p-[12px] relative w-[214px] rounded-[10px] text-white'
											style={{
												backgroundImage: `url(${ImgPath.BgRectangleLight})`,
												backgroundRepeat: 'no-repeat',
												backgroundSize: 'cover',
												backgroundPosition: 'center',
												backgroundSize: '550%',
												// boxShadow: '0px 4px 21px -2px #3F3E3E26'
											}}
										>
											<div className='mb-3 w-full h-[52px] rounded-[10px]'>
												<img className='h-full w-full object-cover object-center' src={ImgPath.MenuSwitchTooltipImageLight} />
											</div>
											<div className='mb-1.5 flex items-start justify-between gap-4'>
												<LanguageTranslator tag='p' className='mb-0 text-sm font-medium'>Prefer the Classic Look?</LanguageTranslator>
											</div>
											<LanguageTranslator tag='p' className='mb-0 text-[10px] whitespace-normal'>
												You can return to the old layout for now. But keep in mind—after 30 days, the new navigation will take over permanently.
											</LanguageTranslator>

											{/* triangle */}
											<div class="absolute bottom-[50px] right-full w-0 h-0 border-y-[10px] border-y-transparent border-r-[12px] border-r-[#6D6FD7]"></div>
										</div>

									</div>
									}
								</div>}

								{/* Render the workspace icon */}
								{uType === 'ENT' && expandMode !== 'workspace' && expandMode != "reports" && !isHoveringOverAdminMaster && renderYourWorkspaceButton()}

								{/* Render the Admin & Master button */}
								{uType === 'ENT' && expandMode !== 'workspace' && expandMode != "reports" && !isHoveringOverAdminMaster && renderAdminMasterButton()}

								{/* Experience New Portal button for Vendor */}
								{tokenData?.uType?.toUpperCase() == "VENDOR" && expandMode != "reports" && !isHoveringOverAdminMaster && (
									isMenuExpanded?.() ? (
										<div 
											className='tooltip-target group relative my-2 mx-3 px-3 py-1.5 h-[34px] flex items-center justify-between gap-2 rounded-lg bg-[#28282D] border border-[#FFFFFF1F] text-white cursor-pointer hover:bg-[#33333A] hover:border-primary transition-all duration-150 mb-1 shadow-sm'
											onClick={() => {
												const hostname = window.location.hostname;

												const baseUrl =
													(hostname === "localhost" || hostname === "quality.supplymint.com")
														? "quality-vendor.supplymint.com"
														: "vendor.supplymint.com";

												const token = sessionStorage.getItem("token");

												const url = `https://${baseUrl}/#/?token=${encodeURIComponent(token || "")}`;

												window.open(url, "_blank");
											}}
										>
											<div className='flex items-center gap-2 overflow-hidden'>
												<Sparkles size={14} className='text-primary shrink-0' />
												<span className='text-xs font-medium whitespace-nowrap text-white'>Experience New Portal</span>
											</div>
											<div className='flex items-center gap-1.5 shrink-0'>
												<span className='w-1.5 h-1.5 rounded-full bg-primary shrink-0 animate-pulse' />
												<span className='text-[10px] font-bold text-white tracking-wider leading-none'>BETA</span>
												<MoveUpRight size={13} strokeWidth={2.2} className='text-white/80 shrink-0 group-hover:text-primary group-hover:translate-x-[1px] group-hover:-translate-y-[1px] transition-all duration-150' />
											</div>
										</div>
									) : (
										<div 
											className='tooltip-target group relative my-2 mx-auto w-[52px] h-[32px] flex items-center justify-center gap-1 rounded-[6px] bg-[#28282D] border border-[#FFFFFF1F] text-white cursor-pointer hover:bg-[#33333A] hover:border-primary transition-all duration-150 mb-1 shadow-sm'
											onClick={() => {
												const hostname = window.location.hostname;

												const baseUrl =
													(hostname === "localhost" || hostname === "quality.supplymint.com")
														? "quality-vendor.supplymint.com"
														: "vendor.supplymint.com";

												const token = sessionStorage.getItem("token");

												const url = `https://${baseUrl}/#/?token=${encodeURIComponent(token || "")}`;

												window.open(url, "_blank");
											}}
										>
											{/* <span className='w-1.5 h-1.5 rounded-full bg-primary shrink-0 animate-pulse' /> */}
											<span className='text-[10px] font-bold text-white tracking-wider leading-none select-none pl-[3px]'>NEW</span>
											<MoveUpRight size={10} strokeWidth={2.2} className='text-white/80 shrink-0 group-hover:text-primary group-hover:translate-x-[1px] group-hover:-translate-y-[1px] transition-all duration-150' />
											<span className='mainmenu-tooltip whitespace-nowrap z-[999] top-1/2 -translate-y-1/2 flex items-center gap-1.5'>
												<Sparkles size={13} className='text-primary shrink-0' />
												<span>Experience New Portal</span>
												<div className='flex items-center gap-1.5 shrink-0'>
													<span className='w-1.5 h-1.5 rounded-full bg-primary shrink-0 animate-pulse' />
													<span className='text-[10px] font-bold text-white tracking-wider leading-none'>BETA</span>
												</div>
											</span>
										</div>
									)
								)}

							</div>
						</React.Fragment>}

					</div>
				</div>

			</React.Fragment >

		)
	}

	// console.log('ExpandModeunique1111', expandMode)

	let getMenuWidth = ()=>{
		if(isMenuExpanded?.()){
			if(expandMode === 'reports') return 310;
			else if(expandMode === 'settings') return 225;
			else return 232;
		}

		return 63;
		
	}
	
	return (
		<div
			key={"mainmenuCheck"}
			style={{
				width: getMenuWidth(),
				boxShadow: menuExpanded ? '4px 4px 4px 0px #00000030' : '',
				height: `calc(100% - ${stripHeight}px)`
			}}
		className={`main-menu flex flex-col w-auto bg-[#212121] fixed z-[990] whitespace-nowrap duration-200 ${isMenuExpanded?.() ? 'rounded-r-[5px]' : ''} top-${stripHeight}`}
		onMouseLeave={(e) => {
			// Check if the mouse is leaving to the expand button area
			const rect = e.currentTarget.getBoundingClientRect();
			const expandButtonArea = {
				top: rect.top + 19,
				left: rect.left + 55,
				right: rect.left + 75,
				bottom: rect.top + 39
			};
			
			// Don't collapse if mouse is over the expand button
			const isOverButton = (
				e.clientX >= expandButtonArea.left &&
				e.clientX <= expandButtonArea.right &&
				e.clientY >= expandButtonArea.top &&
				e.clientY <= expandButtonArea.bottom
			);
			
			if (isOverButton) return;
			
			setTimeout(() => {
				if (expandMode !== "reports" && expandMode !== 'settings') {
					setExpandMode("normal");
					setMenuExpanded(false);
				}
				setIsHoveringOverAdminMaster(false)
				setOverlayActiveModule('ADMIN')
			}, 100);
		}}
		>
			{/* Render the menu */}
			{renderMenu()}
		</div>
	)
}

// Function : To get the most basic first path for the current workspace
const useMostBasicFirstPathForTheCurrentWorkspace = () => {
	let { userDetails, setUserDetails } = useContext(UserDetailsContext)
	// get the first url as per the modules list
	const getFirstPath = (details = userDetails || {}) => {
		const dbData = details?.["current_main_menu_local_config"] || {};

		const getFirstActivePath = (data = []) => {
			let pagePath = "";
			data?.map(item => {
				if (!pagePath) {
					if (item.pageUrl) {
						pagePath = item.pageUrl;
					} else {
						item?.subModules?.map(val => {
							if (val?.pageUrl && !pagePath) {
								pagePath = val.pageUrl;
							}
						})
					}
				}
			})
			return pagePath;
		}

		let pagePath = "";
		if (["settings"]?.includes(dbData?.expandMode)) {
			let moduleList = details?.mainMenu ? JSON.parse(details?.mainMenu)?.moduleList || [] : {}
			pagePath = getFirstActivePath(moduleList?.[0]?.[0]?.subModules)
		} else {
			let dashboardModules = details?.mainMenu ? JSON.parse(details?.mainMenu)?.dashboardModules || [] : {}
			pagePath = getHomeLink(dashboardModules || []);
		}
		return pagePath?.replace("#", "");
	}

	return { getFirstPath };
}

const SuspenseImage = ({ src, alt, className = '', skeletonClassName = '' }) => {
	const [loaded, setLoaded] = useState(false);

	return (
		<div className={`relative`}>
			{!loaded && (
				<div className={`generic-skeleton-dark !h-[34px] !w-[34px] absolute inset-0 ${skeletonClassName}`}></div>
			)}
			<img
				src={src}
				alt={alt}
				loading="lazy"
				onLoad={() => setLoaded(true)}
				onError={() => setLoaded(true)} // hide loader even on error
				className={`${loaded ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'} transition-opacity ${className}`}
			/>
		</div>
	);
};

export { MainMenu, useMostBasicFirstPathForTheCurrentWorkspace, getDashboardPath }